import React from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { styled, Tooltip, Typography, type TypographyProps } from '@mui/material';

import useCanOpenPage from 'app/providers/acl/useCanOpenPage';

export type TextProps = {
	/** The tooltip's text */
	tooltip?: string | React.ReactNode;
	/** An uri that this component can redirect to, if provided */
	to?: string | { module: string; uid: string };
	/** The component rendered if the children prop is not set */
	fallback?: React.ReactNode;
	/** If true, the text will not wrap, but instead will truncate with a text overflow ellipsis. */
	noWrap?: boolean;
	/** Function used if "to" is not set to perform an action if the text is pressed */
	onClick?: React.MouseEventHandler<HTMLSpanElement>;
} & Pick<TypographyProps, 'className' | 'children' | 'sx' | 'variant' | 'align' | 'color' | 'gutterBottom'>;

const StyledLink = styled(Link)(({ theme }) => ({
	// display: 'inline-grid',
	overflow: 'hidden',
	'&:not([role=button]):not(.MuiButtonBase-root)': {
		color: 'inherit',
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'none'
		}
	}
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
	// wordBreak: 'break-all',
	overflow: 'hidden',
	'.Text-noValue': {
		color: theme.palette.text.disabled
	}
}));

/** A component used to render text, with redirect capabilities */
function Text({
	className,
	children,
	tooltip,
	to,
	fallback,
	sx,
	variant,
	color,
	align,
	noWrap = true,
	onClick
}: TextProps) {
	const { canOpenDetailsPage } = useCanOpenPage();

	const url = to ? (typeof to !== 'string' ? canOpenDetailsPage(to?.module, to?.uid) : to) : undefined;

	className = children ? className : clsx(className, 'Text-noValue');
	children = children || fallback;

	const ref = React.createRef<HTMLSpanElement>();
	const [isOverflown, setIsOverflown] = React.useState(false);

	let tooltipTitle = tooltip || children;
	if (typeof tooltipTitle === 'string') {
		const tooltipTitleSubstring = tooltipTitle.substring(0, 600);
		tooltipTitle = `${tooltipTitleSubstring}${tooltipTitleSubstring.length === 600 ? '...' : ''}`;
	}

	React.useEffect(() => {
		if (ref && noWrap) {
			setTimeout(() => {
				setIsOverflown(
					(ref.current?.scrollWidth || 0) > (ref.current?.clientWidth || 0) ||
						(ref.current?.scrollHeight || 0) > (ref.current?.clientHeight || 0)
				);
			}, 0);
		}
	}, [noWrap, ref]);

	const content = url ? (
		<StyledLink to={url}>
			<StyledTypography
				ref={ref}
				className={className}
				sx={sx}
				variant={variant}
				color={color}
				align={align}
				noWrap={noWrap}
			>
				{children}
			</StyledTypography>
		</StyledLink>
	) : (
		<StyledTypography
			ref={ref}
			className={clsx(!!onClick && 'cursor-pointer', className)}
			sx={sx}
			variant={variant}
			color={color}
			align={align}
			onClick={onClick}
		>
			{children}
		</StyledTypography>
	);

	return isOverflown ? (
		<Tooltip title={tooltipTitle} arrow>
			{content}
		</Tooltip>
	) : (
		content
	);
}

export default React.memo(Text);
